import { theme } from "styles/theme";
import styled from "styled-components";

/**
 * Wrapper do componente Input
 * Define o layout geral do campo de entrada, ajustando largura e alinhamento.
 */
export const StyledInputWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "fullWidth",
})<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
`;

/**
 * Label do Input
 * Exibe o texto acima do campo de entrada.
 */
export const InputLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ theme }) => theme.colors.darkGray};
`;

/**
 * Container do Input
 * Configura o layout interno, bordas, background e estados como erro e desabilitado.
 */
export const StyledInputContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "hasIcon",
      "isError",
      "borderColor",
      "backgroundColor",
      "disabled",
      "variant",
      "size",
      "isFakeFocus",
    ].includes(prop),
})<{
  hasIcon: boolean;
  isError: boolean;
  borderColor: keyof typeof theme.colors | keyof typeof theme.colors.neutral;
  backgroundColor:
    | keyof typeof theme.colors
    | keyof typeof theme.colors.neutral;
  disabled: boolean;
  variant: "default" | "styled";
  size: "small" | "medium" | "large";
  isFakeFocus?: boolean;
}>`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  border: 1px solid
    ${({ theme, borderColor, isError }) =>
      isError ? theme.colors.red : theme.colors[borderColor]};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ theme, disabled, backgroundColor }) =>
    disabled ? theme.colors.lightGray : theme.colors[backgroundColor]};
  box-shadow: ${({ variant, theme }) =>
    variant === "styled" ? theme.shadows.light.medium : "none"};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.transparent};
  }

  ${({ isFakeFocus }) =>
    isFakeFocus &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 21%;
      left: -2px;
      width: 2px;
      height: 60%;
      background-color: white;
      animation: blink 1s steps(2, start) infinite;
      pointer-events: none;
    }

    @keyframes blink {
      to {
        visibility: hidden;
      }
    }
  `}
`;

/**
 * Campo de entrada (Input)
 * Configurações dinâmicas de tamanhos, cores do texto e do placeholder.
 */
export const StyledInput = styled.input.withConfig({
  shouldForwardProp: (prop) =>
    !["size", "hasIcon", "textColor", "placeholderColor"].includes(prop),
})<{
  size: "small" | "medium" | "large";
  hasIcon: boolean;
  textColor: keyof typeof theme.colors;
  placeholderColor: keyof typeof theme.colors;
}>`
  flex: 1;
  font-size: ${({ theme, size }) =>
    size === "small"
      ? theme.fontSizes.xs
      : size === "large"
        ? theme.fontSizes.md
        : theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  border: none;
  outline: none;
  color: ${({ theme, textColor }) => theme.colors[textColor]};

  &::placeholder {
    color: ${({ theme, placeholderColor }) => theme.colors[placeholderColor]};
  }

  &:focus::placeholder {
    color: transparent;
  }
`;

/**
 * Wrapper do Ícone
 * Posiciona ícones opcionais dentro do campo.
 */
export const IconWrapper = styled.span<{ size: "small" | "medium" | "large" }>`
  margin-right: ${({ theme, size }) =>
    size === "large" ? theme.spacing(1.5) : theme.spacing(1)};
`;

/**
 * Texto de erro
 * Exibe mensagens de erro abaixo do campo.
 */
export const ErrorText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.red};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;
